import { dynamicFilterGroupWidgetMap } from "app/pages/sales-intelligence/domain/search/types/dynamic-filter-options";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

import { DynamicGroupFilterConditionsResultView } from "../../components/filters/DynamicGroupFilter/DynamicGroupFilterConditionsResultView";
import { DynamicGroupFilterConditionsView } from "../../components/filters/DynamicGroupFilter/DynamicGroupFilterConditionsView";
import { DynamicGroupFilterResultView } from "../../components/filters/DynamicGroupFilter/DynamicGroupFilterResultView";
import { DynamicGroupFilterView } from "../../components/filters/DynamicGroupFilter/DynamicGroupFilterView";
import { DynamicFieldFilter } from "../../models/DynamicFieldFilter";
import type { ConditionSides } from "../../models/DynamicGroupFilter";
import { DynamicGroupFilter } from "../../models/DynamicGroupFilter";
import type { DynamicFieldDescriptor } from "../../types/dynamicFieldDescriptor";
import type { SearchFilterInstance } from "../../types/searchFilterInstance";

import { WIDGET_VIEWS } from "./const";
import type { DynamicFiltersContext, IDynamicFiltersAdapter, WidgetViewPair } from "./types";
import { toDescriptor } from "./utils";

export const createDynamicFiltersAdapter = (): IDynamicFiltersAdapter => {
    const buildInstance = (
        descriptor: DynamicFieldDescriptor,
        { view, resultView }: WidgetViewPair,
        ctx: DynamicFiltersContext,
    ): SearchFilterInstance => ({
        key: ctx.toKey(descriptor.key),
        trackingName: ctx.trackingName,
        category: ctx.category,
        apiCategory: ctx.apiCategory,
        title: ctx.title,
        filter: new DynamicFieldFilter(descriptor.dataType, { options: descriptor.options }, descriptor.metadata),
        filterView: view,
        filterResultView: resultView,
    });

    const toInstance = (descriptor: DynamicFieldDescriptor, ctx: DynamicFiltersContext): SearchFilterInstance =>
        buildInstance(descriptor, WIDGET_VIEWS[descriptor.widget], ctx);

    const createFromDescriptors = (
        descriptors: readonly DynamicFieldDescriptor[],
        ctx: DynamicFiltersContext,
    ): readonly SearchFilterInstance[] => descriptors.map((descriptor) => toInstance(descriptor, ctx));

    const createGroup = (
        groupKey: string,
        descriptors: readonly DynamicFieldDescriptor[],
        ctx: DynamicFiltersContext,
        conditionSides?: ConditionSides,
    ): readonly SearchFilterInstance[] => {
        // conditionSides registers each field's exclude-side variant as a sibling member (identity = field +
        // side). Only modal groups may receive it — inline views iterate getFields() and would render
        // every variant as its own control.
        const members = createFromDescriptors(descriptors, ctx).flatMap((member) => {
            const excludeKey = conditionSides?.variantKeyOf(member.key, inclusionOptionMap.excludeOnly);

            return excludeKey && excludeKey !== member.key ? [member, { ...member, key: excludeKey }] : [member];
        });

        // A single field renders as an ordinary card — no composite wrapper. A modal group holds a list of
        // conditions and is always composed, even single-field.
        const isModal = ctx.widget === dynamicFilterGroupWidgetMap.modal;
        if (members.length <= 1 && !isModal) {
            return members;
        }

        return [
            {
                key: ctx.toKey(groupKey),
                trackingName: ctx.trackingName,
                category: ctx.category,
                apiCategory: ctx.apiCategory,
                title: ctx.title,
                filter: new DynamicGroupFilter(members, conditionSides, ctx.title),
                filterView: isModal ? DynamicGroupFilterConditionsView : DynamicGroupFilterView,
                filterResultView: isModal ? DynamicGroupFilterConditionsResultView : DynamicGroupFilterResultView,
            },
        ];
    };

    return {
        toDescriptor,
        createFromDescriptors,
        createGroup,
    };
};
